import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Eye, EyeOff, Shield, Users, Award } from 'lucide-react';
import axios from 'axios';

const AcceptInvitationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('No invitation token provided');
    }
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null); // Clear error when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('🔐 Accepting invitation...');
      
      // Use axios directly (not api utility) since user isn't authenticated yet
      const response = await axios.post('https://www.receevit.com/api/users/accept-invite', {
        token,
        password: formData.password
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('✅ Invitation accepted:', response.data);
      
      if (response.data.success && response.data.token) {
        // Store JWT token in localStorage for auto-login
        localStorage.setItem('token', response.data.token);
        console.log('✅ Token stored, redirecting to dashboard...');
        
        setSuccess(true);
        // Auto-redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate('/portal');
        }, 2000);
      } else {
        setError(response.data.error || 'Invitation acceptance failed');
      }
    } catch (error) {
      console.error('❌ Invitation acceptance error:', error);
      setError(
        error.response?.data?.error || 
        error.response?.data?.message || 
        'Failed to accept invitation. The link may be expired or invalid.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  const trustFeatures = [
    {
      icon: Shield,
      title: 'Secure Account',
      description: 'Your account is protected with enterprise-grade security.'
    },
    {
      icon: Users,
      title: 'Team Collaboration',
      description: 'Work together seamlessly with your team on invoices and payments.'
    },
    {
      icon: Award,
      title: 'Professional Tools',
      description: 'Access powerful features to manage your financial operations.'
    }
  ];

  if (success) {
    return (
      <div className="pt-16">
        <section className="relative overflow-hidden bg-[#1F11ED] py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="max-w-2xl mx-auto"
            >
              <CheckCircle className="h-20 w-20 text-green-400 mx-auto mb-6" />
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Welcome to Receevit®!
              </h1>
              <p className="text-xl text-blue-100 mb-8">
                Your account has been successfully set up. Taking you to your dashboard...
              </p>
              <button
                onClick={() => navigate('/portal')}
                className="inline-flex items-center px-8 py-3 bg-white text-[#1F11ED] rounded-lg font-semibold hover:bg-blue-50 transition-colors"
              >
                Go to Dashboard Now
              </button>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="pt-16">
        <section className="relative overflow-hidden bg-[#1F11ED] py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="max-w-2xl mx-auto"
            >
              <XCircle className="h-20 w-20 text-red-400 mx-auto mb-6" />
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Invalid Invitation Link
              </h1>
              <p className="text-xl text-blue-100 mb-8">
                No invitation token provided. Please use the link from your invitation email.
              </p>
              <button
                onClick={handleGoToLogin}
                className="inline-flex items-center px-8 py-3 bg-white text-[#1F11ED] rounded-lg font-semibold hover:bg-blue-50 transition-colors"
              >
                Go to Login
              </button>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="pt-16">
      <Helmet>
        <title>Accept Invitation | Receevit®</title>
        <meta name="description" content="Accept your Receevit® invitation, set your password, and start managing invoices and payments securely." />
        <link rel="canonical" href="https://www.receevit.com/accept-invite" />
        <meta property="og:title" content="Accept Invitation | Receevit®" />
        <meta property="og:description" content="Accept your Receevit® invitation, set your password, and start managing invoices and payments securely." />
        <meta property="og:url" content="https://www.receevit.com/accept-invite" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.receevit.com/og-image.png" />
      </Helmet>
      {/* Hero Section with Form */}
      <section className="relative overflow-hidden bg-[#1F11ED] py-24">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute top-40 left-40 w-80 h-80 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            
            {/* Left Side - Welcome Message */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="text-white"
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Complete Your{' '}
                <span className="bg-gradient-to-r from-blue-300 to-white bg-clip-text text-transparent">
                  Account Setup
                </span>
              </h1>
              <p className="text-xl text-blue-100 mb-8">
                You've been invited to join Receevit®! Set your password below to get started managing invoices, payments, and more.
              </p>
              
              {/* Trust Indicators */}
              <div className="space-y-4">
                {trustFeatures.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                    className="flex items-start space-x-3"
                  >
                    <feature.icon className="w-6 h-6 text-blue-300 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-white">{feature.title}</h3>
                      <p className="text-blue-200 text-sm">{feature.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right Side - Password Setup Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-white rounded-2xl shadow-2xl p-8 lg:p-10"
            >
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Set Your Password</h2>
                <p className="text-gray-600">Create a secure password to access your account</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Password Field */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent pr-12"
                      placeholder="Enter your password (min 8 characters)"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Must be at least 8 characters</p>
                </div>

                {/* Confirm Password Field */}
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent pr-12"
                      placeholder="Re-enter your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-red-50 border border-red-200 rounded-lg p-4"
                  >
                    <div className="flex items-center">
                      <XCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
                      <p className="text-red-700 text-sm">{error}</p>
                    </div>
                  </motion.div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#1F11ED] text-white py-3 px-6 rounded-lg font-semibold hover:bg-[#1709C9] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      Setting up your account...
                    </>
                  ) : (
                    'Complete Setup'
                  )}
                </button>
              </form>

              {/* Already have account link */}
              <div className="mt-6 text-center">
                <button
                  onClick={handleGoToLogin}
                  className="text-[#1F11ED] hover:text-[#1709C9] text-sm font-medium"
                >
                  Already have an account? Login here
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 bg-[#1F11ED]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              Why Teams Choose Receevit®
            </h2>
            <p className="text-lg text-blue-100 max-w-2xl mx-auto">
              Join thousands of businesses streamlining their invoice and payment management
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: 'Enterprise Security',
                description: 'Bank-level encryption and secure payment processing through Stripe.'
              },
              {
                icon: CheckCircle,
                title: 'Automated Workflows',
                description: 'Auto-approve invoices, collect payments on due dates, and manage tasks effortlessly.'
              },
              {
                icon: Users,
                title: 'Team Collaboration',
                description: 'Built-in chat, shared access, and role-based permissions for your team.'
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:bg-white/20 transition-all"
              >
                <feature.icon className="w-12 h-12 text-white mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-blue-100">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AcceptInvitationPage;
